import { useRef, useState } from 'react';

import { getMessageChatIa, getTokenChatIa } from 'services/chatIa.service';
import { IChatIa } from 'types/chatIa';

export const ABERTURA = 'Olá! Como posso ajudar?';
export const MENSAGEM_DE_ERRO = 'Ocorreu um erro, por favor recarregue a página e tente novamente!';

export interface MensagemDoChat {
  role: 'user' | 'assistant';
  content: string;
  /** A resposta de abertura do JJ (não tem "Pedir outra resposta"). */
  abertura?: true;
}

type Contexto = IChatIa['data']['context'] | Record<string, never>;

const CONVERSA_INICIAL: MensagemDoChat[] = [{ role: 'assistant', content: ABERTURA, abertura: true }];

/** CHAT-08: o pedido de outra resposta repete a última pergunta do funci. */
export function pedidoDeOutraResposta(ultimaPergunta: string) {
  return `não gostei da sua resposta anterior, pode detalhar melhor sobre: ${ultimaPergunta}`;
}

/**
 * Estado da conversa com o assistente JJ: mensagens, contexto do agente, envio, pedido de outra
 * resposta e encerramento (CHAT-02 a CHAT-08, CHAT-24).
 */
export function useConversaComJJ() {
  const [mensagens, setMensagens] = useState<MensagemDoChat[]>(CONVERSA_INICIAL);
  const [respondendo, setRespondendo] = useState(false);
  // O agente guarda a conversa no `context` que devolve; a próxima pergunta o manda de volta.
  const contexto = useRef<Contexto>({});
  // Cada encerramento abre uma conversa nova: a resposta de uma conversa encerrada é descartada.
  const conversa = useRef(0);

  function responder(texto: string) {
    // Resposta vazia não vira balão (edge case da spec).
    if (texto) setMensagens(atuais => [...atuais, { role: 'assistant', content: texto }]);
  }

  async function perguntar(texto: string) {
    const desta = conversa.current;
    setRespondendo(true);
    try {
      const token = await getTokenChatIa(texto);
      const resposta = await getMessageChatIa({ data: { input: texto, context: contexto.current } }, token[0].access_token);
      if (desta !== conversa.current) return;
      contexto.current = resposta.data.context;
      responder(resposta.data.output.text[0]);
    } catch (erro) {
      if (desta !== conversa.current) return;
      console.error(erro);
      responder(MENSAGEM_DE_ERRO);
    } finally {
      if (desta === conversa.current) setRespondendo(false);
    }
  }

  /** CHAT-03: devolve se a mensagem foi enviada (vazia ou com resposta pendente, não vai). */
  function enviar(texto: string) {
    const pergunta = texto.trim();
    if (!pergunta || respondendo) return false;

    setMensagens(atuais => [...atuais, { role: 'user', content: pergunta }]);
    perguntar(pergunta);
    return true;
  }

  function pedirOutraResposta() {
    const ultimaPergunta = [...mensagens].reverse().find(mensagem => mensagem.role === 'user');
    if (!ultimaPergunta || respondendo) return;

    perguntar(pedidoDeOutraResposta(ultimaPergunta.content));
  }

  function encerrar() {
    conversa.current += 1;
    contexto.current = {};
    setMensagens(CONVERSA_INICIAL);
    setRespondendo(false);
  }

  return { mensagens, respondendo, enviar, pedirOutraResposta, encerrar };
}
